import { useEffect, useRef, useState } from 'react';

import { Button, Group, Modal, Stack, Stepper } from '@mantine/core';

import type { RegistryOrderSubject } from '@/hooks/useRegistryOrderSubjects';
import { RegistryOrderError } from '@/services/registry-order-error';
import type {
  RegistryOrderBatchSubmit,
  RegistryOrderCreateRequest,
  RegistryOrderDetail,
} from '@/types/registry-documents';
import { BatchConfirmStep } from './BatchConfirmStep';
import { CompaniesStep, EveryCompanySwitch } from './CompaniesStep';
import { CompanyStep } from './CompanyStep';
import { ConfirmStep } from './ConfirmStep';
import { DocumentsStep } from './DocumentsStep';
import { batchPayloads, buildOrderPayload } from './orderDraft';
import { SharedDocumentsStep } from './SharedDocumentsStep';
import { useBatchSubmit } from './useBatchSubmit';
import { useOrderCompanies } from './useOrderCompanies';
import { useOrderCompany } from './useOrderCompany';
import { useOrderDocuments } from './useOrderDocuments';
import { useOrderSharedDocuments } from './useOrderSharedDocuments';
import { useOrderSubmit } from './useOrderSubmit';

interface RegistryOrderModalProps {
  opened: boolean;
  onClose: () => void;
  reportId: string;
  /** The report's company subjects; the first is the default prefill. */
  subjects: RegistryOrderSubject[];
  subjectsLoading?: boolean;
  /** Why the subjects could not be read, if they could not — ordering
   * still works by hand, so this is a notice, not a block. */
  subjectsError?: Error | null;
  /** Decides the confirm copy: an activated report sends the order at
   * once, one in triage sends it at Start. `null` when the report's stage
   * is not known — the server decides, and the copy promises neither. */
  reportActivated: boolean | null;
  /**
   * Prepares the order. Throws on failure — a `RegistryOrderError` for the
   * API's outcomes — and the modal stays open showing it against the
   * selection that caused it.
   */
  onSubmit: (payload: RegistryOrderCreateRequest) => Promise<RegistryOrderDetail>;
  /**
   * Prepares one order per company when the analyst orders for every
   * company on the report. Without it the switch is not offered.
   */
  onSubmitMany?: RegistryOrderBatchSubmit;
  /** Called after a successful order, once the modal has closed. */
  onOrdered?: (order: RegistryOrderDetail) => void;
}

function submitLabel(reportActivated: boolean | null): string {
  if (reportActivated === null) {
    return 'Confirm order';
  }
  return reportActivated ? 'Place order' : 'Prepare order';
}

function batchLabel(reportActivated: boolean | null, count: number): string {
  const orders = `${count} ${count === 1 ? 'order' : 'orders'}`;
  if (reportActivated === null) {
    return `Confirm ${orders}`;
  }
  return reportActivated ? `Place ${orders}` : `Prepare ${orders}`;
}

/** What the footer needs from whichever flow drives the steps. */
interface StepFlow {
  canLeaveCompany: boolean;
  leaveCompany: () => void;
  canLeaveDocuments: boolean;
  reviewLabel: string;
  submitLabel: string;
  confirm: () => void;
}

/**
 * Order official company documents from Armadillo for a report.
 *
 * Three steps: the company (found in Armadillo's index, or described by
 * hand when the search cannot find it), the documents from that country's
 * catalogue with price and turnaround, and a confirmation that says when
 * the order is sent. Nothing is billed until a document is delivered, but
 * an order sent to Armadillo is real work for its research desk — hence
 * the explicit confirm and the duplicate guard.
 *
 * With several company subjects on the report the first step offers to
 * order for every one of them: the same documents, one order per company,
 * placed one after another with each outcome shown against its company.
 */
export function RegistryOrderModal({
  opened,
  onClose,
  reportId,
  subjects,
  subjectsLoading = false,
  subjectsError = null,
  reportActivated,
  onSubmit,
  onSubmitMany,
  onOrdered,
}: Readonly<RegistryOrderModalProps>) {
  const [step, setStep] = useState(0);
  const [everyCompany, setEveryCompany] = useState(false);
  // Offered when there is more than one company to order for and the
  // mount site can place a batch.
  const batchable = subjects.length >= 2 && onSubmitMany !== undefined;
  const batch = batchable && everyCompany;

  const company = useOrderCompany({ opened, reportId, subjects });
  const documents = useOrderDocuments(opened, company.country, step >= 1 && !batch);
  const submission = useOrderSubmit({ opened, onSubmit, onClose, onOrdered });
  const companies = useOrderCompanies({ opened, subjects });
  const shared = useOrderSharedDocuments(opened, companies.countries, step >= 1 && batch);
  const batchSubmit = useBatchSubmit({ opened, onSubmitMany, onOrdered });

  // Cleared on close so reopening never inherits a previous selection.
  useEffect(() => {
    if (!opened) {
      setStep(0);
      setEveryCompany(false);
    }
  }, [opened]);

  // An order Armadillo holds without every document must be cancelled with
  // them first; anything else can be tried again — the duplicate guard
  // refuses a second open order for the same documents.
  const retryBlocked =
    submission.error instanceof RegistryOrderError &&
    submission.error.kind === 'accepted_without_documents';

  // Moving between steps changes the selection: an error about the old
  // one must not gate the new one. The one exception is the block above —
  // stepping back and forth must not lift it.
  const goToStep = (next: number) => {
    if (!retryBlocked) {
      submission.clearError();
    }
    setStep(next);
  };

  const submit = (allowDuplicate: boolean) =>
    void submission.submit(
      buildOrderPayload(
        {
          reportId,
          // Only a subject in the current list: an id left over from an
          // earlier read may not be on this report.
          subjectId: company.subject?.id ?? null,
          country: company.country,
          mode: company.mode,
          candidate: company.candidate,
          manual: company.manual.values,
          // What the confirm step lists, not what was once ticked: a code
          // the loaded catalogue no longer has is not ordered unseen.
          products: documents.chosen.map((product) => product.code),
          notes: documents.notes,
        },
        allowDuplicate
      )
    );

  const startBatch = () =>
    void batchSubmit.start(
      batchPayloads(
        reportId,
        companies.companies,
        shared.chosen.map((product) => product.code),
        shared.notes
      )
    );

  const flow: StepFlow = batch
    ? {
        canLeaveCompany: companies.ready,
        leaveCompany: () => goToStep(1),
        canLeaveDocuments: shared.chosen.length > 0,
        reviewLabel: 'Review orders',
        submitLabel: batchLabel(reportActivated, companies.companies.length),
        confirm: startBatch,
      }
    : {
        canLeaveCompany: company.chosen,
        leaveCompany: () => {
          if (company.validate()) {
            goToStep(1);
          }
        },
        canLeaveDocuments: documents.chosen.length > 0,
        reviewLabel: 'Review order',
        submitLabel: submitLabel(reportActivated),
        confirm: () => submit(false),
      };

  const busy = submission.submitting || batchSubmit.inFlight;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Order registry documents"
      size="lg"
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      closeButtonProps={{ disabled: busy }}
    >
      <Stack>
        <Stepper active={step} size="sm" allowNextStepsSelect={false}>
          <Stepper.Step
            label={batch ? 'Companies' : 'Company'}
            description="Who the documents are for"
          >
            {batchable && <EveryCompanySwitch checked={everyCompany} onChange={setEveryCompany} />}
            {batch ? (
              <CompaniesStep companies={companies} />
            ) : (
              <CompanyStep
                company={company}
                subjects={subjects}
                subjectsLoading={subjectsLoading}
                subjectsError={subjectsError}
              />
            )}
          </Stepper.Step>
          <Stepper.Step
            label="Documents"
            description={batch ? 'Offered in every country' : "From the country's catalogue"}
          >
            {batch ? (
              <SharedDocumentsStep documents={shared} />
            ) : (
              <DocumentsStep documents={documents} />
            )}
          </Stepper.Step>
          <Stepper.Step label="Confirm" description="What is ordered and when">
            {batch ? (
              <BatchConfirmStep
                companies={companies.companies}
                documents={shared}
                reportActivated={reportActivated}
                rows={batchSubmit.rows}
                inFlight={batchSubmit.inFlight}
                onOrderAnyway={(index) => void batchSubmit.retry(index)}
              />
            ) : (
              <ConfirmStep
                company={company}
                documents={documents}
                reportActivated={reportActivated}
                error={submission.error}
                submitting={submission.submitting}
                onOrderAnyway={() => submit(true)}
              />
            )}
          </Stepper.Step>
        </Stepper>

        <StepFooter
          step={step}
          busy={busy}
          finished={batchSubmit.started}
          flow={flow}
          // The block is about the single flow's selection; a batch has its
          // own outcome per company and must not inherit it.
          submitDisabled={batch ? false : retryBlocked}
          onBack={() => goToStep(step - 1)}
          onReview={() => goToStep(2)}
          onClose={onClose}
        />
      </Stack>
    </Modal>
  );
}

interface StepFooterProps {
  step: number;
  busy: boolean;
  /** A batch has been sent: the steps are behind, only Done is left. */
  finished: boolean;
  flow: StepFlow;
  submitDisabled: boolean;
  onBack: () => void;
  onReview: () => void;
  onClose: () => void;
}

function StepFooter({
  step,
  busy,
  finished,
  flow,
  submitDisabled,
  onBack,
  onReview,
  onClose,
}: Readonly<StepFooterProps>) {
  const doneRef = useRef<HTMLButtonElement>(null);

  // The submit button leaves with the steps the moment a batch starts, and
  // Done is disabled until the batch settles, so focus falls to the body
  // meanwhile. Once Done is live it takes the focus, unless the analyst has
  // since put it somewhere else (a row's "Order anyway" that is still there).
  useEffect(() => {
    const active = document.activeElement;
    if (finished && !busy && (active === null || active === document.body)) {
      doneRef.current?.focus();
    }
  }, [finished, busy]);

  if (finished) {
    return (
      <Group justify="flex-end">
        <Button ref={doneRef} onClick={onClose} disabled={busy} data-testid="registry-order-done">
          Done
        </Button>
      </Group>
    );
  }
  return (
    <Group justify="space-between">
      <Button variant="default" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Group>
        {step > 0 && (
          <Button variant="subtle" onClick={onBack} disabled={busy}>
            Back
          </Button>
        )}
        {step === 0 && (
          <Button
            onClick={flow.leaveCompany}
            disabled={!flow.canLeaveCompany}
            data-testid="registry-order-next"
          >
            Choose documents
          </Button>
        )}
        {step === 1 && (
          <Button
            onClick={onReview}
            disabled={!flow.canLeaveDocuments}
            data-testid="registry-order-next"
          >
            {flow.reviewLabel}
          </Button>
        )}
        {step === 2 && (
          <Button
            onClick={flow.confirm}
            loading={busy}
            disabled={submitDisabled}
            data-testid="registry-order-submit"
          >
            {flow.submitLabel}
          </Button>
        )}
      </Group>
    </Group>
  );
}
